import type { Edge, Node } from 'reactflow';

import type {
  AssetKind,
  CopyAssets,
  CopyValidationReport,
  ProducedHistoryItem,
  ProductInfo,
  SlotSubjectInfo,
  StepErrorClass,
  StepStatus,
} from '@/types';

import type { LaneLockKind } from './laneLocks';

/**
 * 卡頂飄浮藥丸吃的相位。`pending_future` 是 D5 遺留的 FE-only 排隊態;
 * `paused`(R275)= job 已暫停/終止而這張卡沒有 in-flight step —— **不得**顯示「生成中」
 * 卻也不是失敗(典型:批次 job 裡別張卡壞了,這張的 step 永遠停在 WAITING)。
 */
export type JobStepPhase = StepStatus | 'pending_future' | 'paused';

/**
 * R275 — 這張卡所屬的生成失敗/停滯資訊(`useLaneLockChannels` 從
 * [cardJobState](@/features/jobs/cardJobState) 派生 patch 進來;render-only、不進 wire)。
 *
 * 有值 = 這張卡**不在跑**(所以 `laneRun` 必須同時缺席,否則卡面同時有進度條與失敗塊)。
 */
export interface LaneFailInfo {
  jobId: string;
  /** `failed` = 這張卡有 FAILED step;`stalled` = job 停了但這張卡自己沒壞。 */
  kind: 'failed' | 'stalled';
  /** 後端原文(Vertex 英文字串);顯示端只收進「詳細資訊」,**不解析字串做分類**。 */
  message?: string;
  code?: string;
  /** 分類判準的唯一來源(`stepFailureKind`);缺席 → 通用文案。 */
  errorClass?: StepErrorClass;
  /** 後端逐 step 的權威值:重跑有沒有機會過。 */
  retryable?: boolean;
  /**
   * R277(docs/98 §2.2/2.3)— BE 繁中主文案 / 建議動作 / 語意代碼 /
   * 「同一份輸入手動重跑有沒有意義」。缺席 = 舊 job → 顯示端退回 errorClass 對照。
   */
  userMessage?: string;
  userAction?: string;
  failureCode?: string;
  /** false = 不給重跑鍵(同一份凍結輸入 continue 必再失敗);undefined = 舊 job 照舊。 */
  userRetryable?: boolean;
  /** true = 壞的是隱藏步(展開型影片卡的內部段落)。 */
  hidden?: boolean;
  /** 能不能「重跑失敗段」(job 仍 PAUSED 才行;cancel 過 = 409)。 */
  canContinue: boolean;
  /** 能不能「停止」(冪等 cancel;終態不行)。 */
  canCancel: boolean;
}

/**
 * Canvas 視覺層的型別 + 不來自 dagService 的常數
 * -----------------------------------------------------------------------------
 * Graph 資料由 :func:`loadGraph` 從 dagService aggregate（R49 per-doc OCC）載入，
 * 並在邊界處轉換成本檔的 ``CanvasNode`` / ``CanvasEdge`` 形狀。
 *
 * 本檔只剩下：
 *   - 視覺層型別（CanvasNode / CanvasEdge / CanvasMode / 各 NodeData）
 *   - 視覺常數（NODE_DIMENSIONS）
 *   - URL 路由 helpers（DEFAULT_PROJECT_ID / projectIdForMode / modeForProjectId）
 */

/**
 * Canvas node 視覺類型(三色世界觀 + creative archetype):
 *
 * - `root` — ELEMENT(橘 — 靈感來源檔案 / 上傳)
 * - `tool` — CONCEPT(綠 — Prompt 描述,可鎖)
 * - `output` — Legacy 成品(舊版 Output 卡;BE 仍用 `output` 作 wire kind)
 * - `creative` — **R-M01 新加** D3 Banana Split CREATIVE(藍 — 已產出影像 / 影片)
 *                **UI-only**:BE-side 仍走 `output` kind;FE 在 mapper / 元件層
 *                把 `output` + asset 顯示成 CREATIVE。等 BE 加 `CREATIVE` enum
 *                才會 wire 真實 kind。
 */
export type NodeKind = 'tool' | 'output';

// R167(F5 終刪)— RootNodeData(橘 ELEMENT 卡 reactflow 層)已刪;存量 ROOT doc
// 由 loadGraph / snapshot merge fail-soft 過濾,不進畫布。


/**
 * ToolNode 的執行狀態（由 ``services.executionService`` 透過 realtimeBus 推送，
 */

export interface ToolNodeData {
  label: string;
  /**
   * AI 工具引擎名稱（VEO 3 / IMAGEN / CROP / CAPTION），在 ``loadGraph`` 從 ``meta`` 解出。
   *
   * **命名注意**:這是 canvas 視覺層的「引擎名」欄位,跟 app 層 `types/node.ts`
   * `ToolNodeData.kind`(discriminated union 的 discriminant,值恆為 `'tool'`)是
   * **兩個不同的東西**。先前兩處同名 `kind` 造成 `ToolNode` 顯示守則
   * `data.kind === 'tool'` 恆 false(canvas 層 `kind` 是引擎名不會等於字面 `'tool'`)。
   * 改名 `engine` 後語意明確,不再跟 discriminant 混淆。
   */
  engine?: string;
  hint?: string;
  /** drop-from-palette 後 800ms 內顯示高亮 */
  justDropped?: boolean;
  /** 目前的執行狀態（無值代表 idle） */
  /**
   * R22:Job 進行中時,此 tool node 對應的 step 狀態。(R168:`runState` 已終刪 — 原由
   * canvas executionService 填、後者由 firestore listener snapshot 經 `useJobListener`
   * 寫 store 後 patch 過來);視覺上同一個 ToolNode 兩個來源不會同時用,但保留兩個
   * 欄位以解耦。
   */
  jobStepStatus?: JobStepPhase;
  /**
   * R228 — 生成列鎖(`hard` = 這張卡正在被生成 / 寫入;`soft` = 正被某次生成當輸入讀取,
   * 唯讀但可被別條列共用)。由 `useLaneLockChannels` 從 `laneLocks.deriveLaneLocks` 派生
   * patch 進來,**render-only、不持久化、不進 wire**(同 `jobStepStatus` 的處理)。
   */
  laneLock?: LaneLockKind;
  /**
   * R228 — 這張卡是某個 job 的 scope 目標時的進度(卡面把「生成」鈕原位換成進度條)。
   * 只有 `laneLock === 'hard'` 的目標卡會有值。
   */
  laneRun?: { jobId: string; completed: number; total: number };
  /**
   * R275 — 這張卡所屬的生成**失敗/停滯**(FB-20260821-0002)。與 `laneRun` **互斥**:
   * 有這欄就代表不在跑,卡面把進度條換成失敗塊(錯誤訊息 + 重跑 / 停止)。
   */
  laneFail?: LaneFailInfo;
  /** R228 — 這張卡正在跑 AI 協助填寫(節點級鎖;紫色呼吸框 + 控件唯讀)。 */
  assistBusy?: boolean;
  /**
   * R256.1 — 這張 COPY 卡正在跑 `POST /text-gen`(節點級;**沒有 job**)。
   *
   * ⚠️ 為什麼不能只靠 `laneRun`:R228 的鎖/進度/卡頂藥丸整條鏈都是**從 job 派生**的,
   * 而 text-gen 是一支同步 REST → 那條鏈一格都不亮(Bryan 2026-08-19 回報「文字按下
   * 生成的時候,那張卡沒有鎖定,也沒有生成中」)。這欄讓卡片走**同一條**視覺通道:
   * `useLaneLockChannels` 同時把 `laneLock` 補成 `hard`,再由本欄補上「真的在跑」
   * (`laneRunActive`)—— 沒有 step 可數,所以**刻意不造假的 `laneRun` 進度條**。
   * render-only、不持久化、不進 wire(同 `assistBusy`)。
   */
  copyGenBusy?: boolean;
  // ─── R26：Stage B Vertex AI typed config（mirror types/node.ts ToolNodeData） ─
  /**
   * 結構化 tool_type — Stage B 嚴格 enum。D5 影片鏈合併節點 `VIDEO_MERGE` 是綠 TOOL
   * (ffmpeg 串接 clip→成片);視覺比照既有綠 TOOL。鏡像 `types/node.ts` ToolNodeData.toolType。
   */
  // @backend-pending(Q1):VIDEO_MERGE tool_type 後端尚未認(live persist 會 422),整合 + feature flag 下個 workflow
  // R134(docs/75)— VIDEO_SHORT_A/B 短影片拼裝卡(畫布可建;submit 期 BE 展開隱藏子圖)。
  // R272(ADR-045)— `VIDEO_REEL` 動態圖文影片綠卡(AI 導演 + HTML 模板渲染)。
  // R399(ADR-052)— `VIDEO_SEEDANCE` 字節 Seedance 影片卡。
  toolType?:
    | 'COPY'
    | 'IMAGE'
    | 'VIDEO'
    | 'VIDEO_OMNI'
    | 'VIDEO_SEEDANCE'
    | 'COMPOSE'
    | 'VIDEO_MERGE'
    | 'VIDEO_REEL'
    | 'VIDEO_SHORT_A'
    | 'VIDEO_SHORT_B';
  /** Per-tool typed config（對齊後端 NodeConfig discriminated union） */
  config?: Record<string, unknown>;
  /** R49 — OCC version base(同 RootNodeData.__occVersion;reactflow 永遠唯讀) */
  __occVersion?: number;
  /** M-03 拍板:選中聚焦視覺(同 RootNodeData.dimmed) */
  dimmed?: boolean;
  /** ADR-026 — green TOOL 產過 asset 後 = true(BE server-owned 唯讀;reactflow 純讀) */
  locked?: boolean;
  /**
   * R134(docs/75 §6)— 短影片 A/B 卡的 server 投影(final 15s asset id;合成 OUTPUT
   * nid == A/B nid → mirror-writeback 落在這張 TOOL 卡上)。FE 據此建真結果藍卡
   * (R135.2 `planVideoShortFinalCards`)。server-owned 唯讀(`NODE_DATA_KEY_GROUPS.
   * runtimeOnly`),一般 TOOL 恆無值。
   */
  projectedAssetIds?: string[];
  // ─── R136(docs/76 / ADR-035 雙色卡)— 綠卡三輸入 + 產物歷史 ───────────────
  /**
   * 需求描述 brief(綠卡頂層 `text_value`;≤10000,初始綠卡必填)。F2 面板編輯;
   * wire 走 top-level 欄(非 config),`appNodePatchToCreativeNodePatch` 既有通道。
   */
  textValue?: string;
  /**
   * 素材區(docs/76 §1.2;圖 ≤3 / 影 ≤1)。勾選結果 = SSOT;PATCH **整包覆寫**
   * (綠卡契約只送 image/video 兩鍵;textValues 為橘卡存量欄,綠卡恆空不送)。
   */
  slots?: {
    videoAssetIds: string[];
    imageAssetIds: string[];
    textValues: string[];
    /** docs/77 §2 角色分格(≤1 each;圖片合計含 legacy ≤3)。optional = 存量相容。 */
    productAssetIds?: string[];
    characterAssetIds?: string[];
    sceneAssetIds?: string[];
    extraAssetIds?: string[];
    /** R404 — Logo 格(≤1;BE 開欄前恆空,gate = catalog `features.logo_slot`)。 */
    logoAssetIds?: string[];
    /** docs/81 §3(R180)— 商品資訊(LWW 整包:PATCH slots 必帶,漏帶 = 清空)。 */
    productInfo?: ProductInfo | null;
    /** docs/97 §5.1(R274.1)— 模特兒 / 場景名稱(LWW 同上;純識別標籤,不進 prompt)。 */
    characterInfo?: SlotSubjectInfo | null;
    sceneInfo?: SlotSubjectInfo | null;
    /** R395(docs/131)— per-asset 名稱(LWW 隨 slots;缺 = {})。 */
    assetAliases?: Record<string, string>;
  };
  /**
   * 產物歷史(docs/76 §5.1;server-owned 唯讀,`runtimeOnly` 剝除)。FE 依 item_id
   * 集合差偵測新筆 → `producedHistoryPlan` 建/綁真藍卡。
   */
  producedHistory?: ProducedHistoryItem[];
  /**
   * R408.2(BE docs/134 / ADR-053)— 使用者刪過承接藍卡的產物 item_id(server-owned 唯讀、
   * version-free,`runtimeOnly` 剝除)。planner 最先跳過這些筆 → 刪掉的藍卡不會被建回來。
   */
  dismissedItemIds?: string[];
}

/**
 * 顯示用的中文狀態（UI 已以此為 key 配色 / 顯示）。
 * 真實後端的 ``OutputStatus`` 是英文（pending / running / done），
 * :func:`loadGraph` 在邊界翻譯。
 */
// eslint-disable-next-line hai/no-hardcoded-cjk -- domain 值:OutputStatus 型別成員(進資料、被 === 比對),非顯示文案;顯示端另走 i18n
export type OutputStatus = '完成' | '生成中' | '待審';

export interface OutputNodeData {
  label: string;
  status: OutputStatus;
  meta?: string;
  /**
   * R158(docs/78 ADR-039)— bound 藍卡的來源綠卡快照(server-owned;唯讀透傳)。
   * **R177(ADR-040 v2)起僅作紀錄**:延伸綠卡不再複製 config(改空白卡 + 夾帶),
   * 只讀 `tool_type` 決定新卡型。
   */
  boundSourceConfig?: { tool_type?: string; config?: Record<string, unknown> };
  /**
   * R177(docs/79 §6 / ADR-040 W3)— bound 藍卡的來源綠卡輸入快照(server-owned;唯讀
   * 透傳)。**延伸夾帶的唯一料源**:建邊當下抄 `slots.product_asset_ids` +
   * `character_asset_ids`(R404 + `logo_asset_ids` + 這幾張的 `asset_aliases`)進新卡
   * slots(extra 不抄)。聚合失敗整欄不存在(fail-soft)。BE 存的是 `green.slots.model_dump()`
   * 整包,所以 BE 開 Logo 欄後這裡自動帶 `logo_asset_ids`。
   */
  boundSourceInputs?: {
    slots?: {
      product_asset_ids?: string[];
      character_asset_ids?: string[];
      /** R404 — Logo 格(BE 開欄後才會出現)。 */
      logo_asset_ids?: string[];
      /** R395 — per-asset 名稱(R395 之前存的快照沒有這欄)。 */
      asset_aliases?: Record<string, string> | null;
      scene_asset_ids?: string[];
      extra_asset_ids?: string[];
      image_asset_ids?: string[];
      video_asset_ids?: string[];
      /** docs/81 §3 — 商品資訊(非 asset、不佔格);R200 面板顯示用。 */
      product_info?: {
        name?: string | null;
        brand?: string | null;
        product_type?: string | null;
        length_cm?: number | null;
        width_cm?: number | null;
        height_cm?: number | null;
      } | null;
    };
    text_value?: string | null;
  };
  /**
   * R200(BE handoff `creative-test-v2/docs/84` §2)— bound 藍卡的**運行紀錄**
   * (server-owned;唯讀)。`OutputSourcePanel`「實際送出」區的資料源。
   *
   * **fail-soft**:BE 聚合失敗則整欄不存在(docs/84 §5 fallback 2)→ 消費端各區獨立判斷;
   * ADR-040 之前綁的舊藍卡也沒有此欄(BE 不 backfill)。
   * `run_ref` / `trace_id` / `refine_asset_gcs_uri` 是回溯用系統欄,**面板不顯示**。
   */
  boundRunRecord?: {
    template_id?: string;
    model?: string;
    resolved_params?: Record<string, unknown>;
    run_ref?: string;
    trace_id?: string;
    refine_asset_gcs_uri?: string;
    /** `{role, prompt}`;role ∈ clip|base|extend|seg1|seg2,BE 已排序、每段 ≤8000 字。 */
    applied_prompts?: { role?: string; prompt?: string }[];
  };
  /**
   * R270 / ADR-044 — **文案藍卡**的 provenance(`config.text_gen_record`,後端寫、唯讀)。
   *
   * ⚠️ **BE 刻意不叫 `run_record`**(docs/95 §4):那個欄位的形狀綁 job/step(圖片/影片走
   * job),而 `/text-gen` 是同步端點**沒有 job** —— 硬塞進 `run_record` 會讓既有的
   * `resolved_params` / `applied_prompts` 解析踩空。所以是獨立一欄、獨立一區。
   */
  boundTextGenRecord?: {
    request_id?: string;
    charged_credits?: number;
    /** ISO(BE 寫 `…Z`)。 */
    generated_at?: string;
    /** 產出它的文字綠卡 nid。 */
    source_node_id?: string;
  };
  /**
   * R61 — 持久化「結果藍卡」攜帶的產出 asset id(BE OUTPUT 節點 top-level `asset_id`)。
   * 有值 = GENERATE 產出的結果卡(每張 asset 一個持久 OUTPUT 節點;孤兒擺位、不建
   * TOOL→卡 edge → 永不被 mirror-writeback 灌 `projected_asset_ids`,無雙重資料)。
   * `OutputNode` 用它走 project-scoped 簽 URL 渲染縮圖 + 一鍵下載;可 Add Concept 續接。
   * reload 後從 BE `asset_id`(或 `config.asset_id` fallback)還原(不依賴 jobId / activeJob)。
   */
  assetId?: string;
  /**
   * R69 — 結果藍卡 asset 種類,reload 後從 OUTPUT `config.asset_kind` 還原(經 nodeAdapter)。
   * `OutputNode` 優先用它選渲染 element(影片 / 圖片),不必再查 in-memory `projectAssetsStore`
   * (reload 後常空 → 縮圖不出來 / 影片誤當圖片渲染);store 查得到時仍可當 fallback。
   */
  assetKind?: AssetKind;
  /**
   * Stage B：佔位符旗標。`true` 表示這是 add concept 程序化生成的「待生成」OUTPUT
   * 佔位（尚無真實素材）— `OutputNode` 渲染端套虛線邊 + muted 文字。loadGraph 端
   * 對 `asset_id` 為空的 OUTPUT 派生 `placeholder=true`（reload 後仍是佔位）；有
   * asset 的 OUTPUT 視為已產出，placeholder=false/undefined。
   */
  placeholder?: boolean;
  /** R22：Job step 狀態（跟 ``status`` 不同 domain — 後者是 output entity 自己的狀態）。 */
  jobStepStatus?: JobStepPhase;
  /**
   * R228 — 生成列鎖(同 ToolNodeData.laneLock)。藍卡最常見的是 `hard`:上游綠卡正在
   * 生成、成品即將寫進這張卡 → 縮圖顯 shimmer 骨架等成品落地。
   */
  laneLock?: LaneLockKind;
  /**
   * R275 — 同 ToolNodeData.laneFail。藍卡只用它**停掉 shimmer**:上游失敗之後那格
   * 掃光會一直掃,而它承諾的「成品即將落地」已經不成立(= 事故的另一半症狀)。
   */
  laneFail?: LaneFailInfo;
  /** R49 — OCC version base(同 RootNodeData.__occVersion;reactflow 永遠唯讀) */
  __occVersion?: number;
  /** M-03 拍板:選中聚焦視覺(同 RootNodeData.dimmed) */
  dimmed?: boolean;
  /**
   * Stage F — 融合節點旗標(FE-only 派生,**非持久化**)。`true` 表示這個 OUTPUT-kind
   * 節點是「融合 / 匯集」變體:由合併模式把 ≥2 個橘 ROOT / 藍 OUTPUT 各拉一條 edge
   * 匯入後產生。視覺走淺藍「融合」卡,且加一顆 Add Concept CTA(融合後面接綠 TOOL)。
   *
   * 為什麼是 OUTPUT-kind(不是 ROOT):OUTPUT 永遠有 target handle(能接收匯入 edge),
   * 不需條件渲染。ROOT 的 target handle 是條件渲染,當 optimistic→server 節點替換 /
   * reload 派生時機 edge 還沒建好 → 沒派生到 fusion → target handle 不見 → 來源→融合
   * 的 edge 無錨點畫不出來。改 OUTPUT 即治本(永遠有錨點)。
   *
   * BE 端零改動:融合身份不寫 wire format(`appNodeToCreateRequest` strip 掉);
   * reload 後由 loadGraph 從「OUTPUT 節點被 root/output(非 tool)餵 incoming edge」
   * 派生回來(正常 output 只被 tool 餵)。
   */
  fusion?: boolean;
  /**
   * Stage F — 融合節點匯集的來源數量(FE-only 派生,非持久化)。建立時 = 被選來源數;
   * reload 後 = 該 node 的 incoming edge 數。顯示成「匯集 N 個」。
   */
  mergedCount?: number;
  /**
   * R77 — FE-only 派生旗標(非持久化):`true` = R61/R62「自建結果藍卡」(建立時寫
   * `config.fe_result_card`,經 `creativeNodeToAppNode` 從 config 派生)。CoreCanvas
   * 結果卡 reconcile 用它把「綠卡有下游 OUTPUT 就 skip」收窄成「只 skip 真空投影 holder」
   * → 自建卡不再反鎖自己,每次生成 append 一張新卡(回復 R61 原始語意)。不寫回 BE。
   */
  isResultCard?: boolean;
  /**
   * §5.5 — 腳本三段編輯版(後端 `PATCH /nodes/{nid}/script` 落盤 `text_value`;段標頭
   * `【開場】…` 為穩定契約,可 `parseScriptParts` 拆)。null / 無 = 無編輯版(顯原始產出)。
   */
  textValue?: string | null;
  /**
   * R104 — AI-5 text-gen 產出的結構化文案(RSA / PMax 7 類),承載於文案成品藍卡。
   * 持久化在 OUTPUT `config.copy_assets`(經 nodeAdapter 邊界 domain ↔ DTO)。有值 =
   * 文案成品卡:`OutputNode` 顯文案摘要(非縮圖),選取後 OutputInspector 渲染 RSA 面板
   * (`CopyRsaOutput`)**唯讀展示**(R162:藍卡文字後端管理,前端不可編輯)。
   */
  copyAssets?: CopyAssets;
  /**
   * R104 — text-gen 生成當下的逐類別報告(截斷 / 則數不足 …);**非持久化**(僅本次
   * session 建卡時帶,advisory)。reload 後不還原(docs/63 §78:報告是生成當下語意)。
   */
  copyReport?: CopyValidationReport;
}


export type CanvasNode =
  | Node<ToolNodeData, 'tool'>
  | Node<OutputNodeData, 'output'>;

export type CanvasEdge = Edge;

export type CanvasMode = 'tree' | 'canvas';

/**
 * 各節點型別的預設視覺尺寸。reactflow 在 DOM 量到實際尺寸前會用這個值算 edge
 * endpoint，避免首幀連線浮空。``loadGraph`` 在轉換 AppNode → CanvasNode 時依
 * ``data.kind`` 套用。
 */
export const NODE_DIMENSIONS: Record<NodeKind, { width: number; height: number }> = {
  // M-03 拍板:ELEMENT 200px(對齊 mockup M-03 line 107),CONCEPT / CREATIVE = 232px
  // height 是 reactflow edge endpoint 計算用 initial seed;真實高度由 node 內容 grow,
  // 改大 height 不會強制撐(reactflow 量 DOM 自動 reflow)。
  //
  // **Output 維持 200px 的理由**(M-03 reviewer Finding 2):
  // - Output 是 R32 schema migration 之前就存在的 Legacy 成品卡片;既有 demo
  //   fixtures + 部分 BE 既存資料的 position 是以 200px 寬度算好的(canvas 自動
  //   tidy 演算法依 width 算 x 距離)。改成 232 寬會在歷史 graph 上看到節點互相
  //   靠太近 / overlap。
  // - M-03 mockup 的 CREATIVE archetype 是「對外 user 看到的新卡」設定 232,跟
  //   Output 是兩個獨立 archetype(BE-side 仍同 `output` kind,FE 透過 mapper
  //   分流);兩者並存,各自尺寸不互相影響。
  // - 等 R48+ 砍 output kind + 全面 migrate 到 creative archetype 後可一次拉齊。
  tool: { width: 232, height: 108 },    // Concept — 創意卡片(D3 標準寬度 232px)
  output: { width: 200, height: 92 },   // Output — Legacy 成品(M-03 維持 200,見上方註解)
};

/**
 * 排版「間距預留」用的寬鬆 fallback 尺寸(Stage G fix:動態尺寸感知排版)
 * -----------------------------------------------------------------------------
 * `NODE_DIMENSIONS` 的 height 是 reactflow edge endpoint 計算用的 **initial seed**
 * (例 root 96 / tool 108),刻意取小,因為真實高度由 node DOM 內容 grow(reactflow
 * 量 DOM 自動 reflow)。但 tidy / placement 的「層間距 / 落點」需要的是**節點實際
 * 佔的視覺高度**,seed 值遠小於真實高度 → 固定層距會撞(Bryan 回報「直向節點重疊」)。
 *
 * 排版邏輯優先用 reactflow 量到的 `node.height/width`(最準);量不到(程序化新建、
 * 尚未掛 DOM)才走這份 fallback。fallback **刻意偏大**(寧可多留空間也不重疊):
 *   - root  含 1:1 縮圖 + Add Concept CTA,實際 ~270 → 取 280
 *   - creative 含 16:9 thumb + 標題 + CTA,實際 ~320 → 取 320
 *   - tool  含 CTA,實際 ~160 → 取 170
 *   - output 結果藍卡 / 填入素材後的佔位卡含 16:9 縮圖 + 雙 CTA,實際 ~290 → 取 290。
 *     (舊值 120 是「Legacy 空成品」估值,但 add concept 佔位填入後會長到 ~290 → 下游
 *      綠節點落點用 120 會被撐大的藍卡蓋住。fusion / legacy 較矮共用此盒會 over-reserve,
 *      屬良性留白 — 留白遠勝重疊。)
 * 寬度沿用視覺寬度(最寬 232);root / output 視覺較窄,但排版用統一 232 較安全。
 */
export const NODE_SIZE_FALLBACK: Record<NodeKind, { width: number; height: number }> = {
  // R142 fix(Bryan dev 回報「預先建立的藍卡出生會重疊」):R140 內容綠卡富卡面
  // 寬 334、實高 ~500+(標題+素材列+描述+比例+進階+CTA),舊 fallback 232×170 讓
  // 自動長的預覽藍卡(childAnchor 依父盒落點)壓在綠卡上。取偏大 340×560;A/B /
  // COMPOSE 等窄卡共用此盒會 over-reserve = 良性留白(本表哲學:留白遠勝重疊)。
  tool: { width: 340, height: 560 },
  output: { width: 232, height: 290 },
};

/**
 * 未知 type 的排版兜底盒(R166:原 `NODE_SIZE_FALLBACK.creative` 值原樣抽出 —
 * creative 節點型別已隨死渲染鏈刪除,此保守偏大盒僅供 placement / tidy 對
 * unknown type 兜底,行為不變)。
 */
export const NODE_SIZE_FALLBACK_DEFAULT = { width: 232, height: 320 } as const;

/** 排版 fallback 寬度(unknown type / 缺值時的保守寬度)。 */
export const FALLBACK_NODE_WIDTH = 232;

/**
 * Demo project id 約定
 * -----------------------------------------------------------------------------
 * ``proj_demo_tree`` / ``proj_demo_canvas`` 兩個 id 留作 router redirect 預設值
 * (``routes.tsx`` 無 projectId 時導向 ``DEFAULT_PROJECT_ID``)。URL 結構為
 * ``/canvas/:projectId``，CanvasPage 從 ``useParams`` 取，沒有時 fallback 到
 * ``DEFAULT_PROJECT_ID``。Mode 由 projectId 後綴推導，**不**再走 ``?mode=`` query
 * — 一個 URL = 一個 source of truth。
 *
 * 真實環境每個 project 是獨立資源，mode 應該是 user 視覺偏好（存 prefsStore），
 * 跟 projectId 解耦。Demo 階段先用「mode 跟 projectId 1:1 綁定」的簡化模型。
 */
export const DEFAULT_PROJECT_ID = 'proj_demo_tree';

const PROJECT_ID_FOR_MODE: Record<CanvasMode, string> = {
  tree: 'proj_demo_tree',
  canvas: 'proj_demo_canvas',
};

export function projectIdForMode(mode: CanvasMode): string {
  return PROJECT_ID_FOR_MODE[mode];
}

export function modeForProjectId(projectId: string): CanvasMode {
  return projectId.endsWith('_canvas') ? 'canvas' : 'tree';
}

